import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ProductDetailPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faTimes,
  faSearchPlus,
  faSearchMinus,
} from "@fortawesome/free-solid-svg-icons";
import {
  getProductById,
  getProductsBySeries,
  getSeriesById,
} from "../../service/galleryService";
import LoadingSpinner from "../LoadingSpinner";
import DOMPurify from "dompurify";
import EnquireModal from "../EnquiryForm";
import { useAuth } from "../../context/AuthContext";
import LoginModal from "../login";
import { Modal, ModalBody } from "react-bootstrap";
import { faHeart as faSolidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import { likeProduct, unLikeProduct } from "../../service/product";

const ProductDetailPage = () => {
  const { seriesId, productId } = useParams();
  const navigate = useNavigate();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [product, setProduct] = useState<any>(null);
  const [series, setSeries] = useState<any>(null);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);
  const [showEnquireModal, setShowEnquireModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user } = useAuth();

  // Image gallery states
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const checkDescriptionOverflow = () => {
    if (descriptionRef.current) {
      const el = descriptionRef.current;
      setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkDescriptionOverflow();
      window.addEventListener("resize", checkDescriptionOverflow);
    }, 300);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkDescriptionOverflow);
    };
  }, [product?.description, showFullDescription]);

  const renderDescription = () => {
    if (!product?.description) return null;

    const cleanHtml = DOMPurify.sanitize(product.description);

    return (
      <>
        <h2 className={styles.descriptionHeader}>Description</h2>
        <div
          ref={descriptionRef}
          className={`${styles.productDescription} ${
            !showFullDescription ? styles.truncated : ""
          }`}
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </>
    );
  };

  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
    setZoomLevel(1);
  };

  const handleWheelZoom = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomLevel((prev) => Math.min(Math.max(prev + delta, 1), 3));
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = "auto";
  };

  const navigateLightbox = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setLightboxIndex((prev) =>
        prev === 0 ? product.picture.length - 1 : prev - 1
      );
    } else {
      setLightboxIndex((prev) =>
        prev === product.picture.length - 1 ? 0 : prev + 1
      );
    }
  };
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);

  useEffect(() => {
    if (relatedProducts.length <= 3 || isCarouselHovered) return;

    const interval = setInterval(() => {
      const nextSlide =
        currentSlide >= relatedProducts.length - 3 ? 0 : currentSlide + 1;
      setCurrentSlide(nextSlide);

      if (carouselRef.current) {
        const scrollAmount = nextSlide * 300; // Adjust based on your card width + gap
        carouselRef.current.scrollTo({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    }, 5000); // Scroll every 5 seconds

    return () => clearInterval(interval);
  }, [currentSlide, relatedProducts.length, isCarouselHovered]);
  const fetchData = async () => {
    try {
      setLoading(true);

      const productData = await getProductById(productId!);
      setProduct(productData);
      setIsLiked(() => product?.hasUpvoted);

      setSeries(productData?.series);

      const relatedData = await getProductsBySeries(seriesId!);
      setRelatedProducts(
        relatedData.data.filter((p: any) => p._id !== productData._id)
      );
    } catch (err: any) {
      setError(err.message || "Failed to load product details");
      console.error("Error loading product:", err);
    } finally {
      setLoading(false);
    }
  };
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    if (productId && seriesId) {
      fetchData();
    }
  }, [productId]);
  const scrollCarousel = (direction: "left" | "right") => {
    if (!carouselRef.current) return;

    const container = carouselRef.current;
    const scrollAmount = direction === "right" ? 300 : -300;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });

    const newSlide =
      direction === "right"
        ? Math.min(currentSlide + 1, relatedProducts.length - 1)
        : Math.max(currentSlide - 1, 0);
    setCurrentSlide(newSlide);
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const toggleLikeProduct = async (productId: any) => {
    try {
      await likeProduct(productId);
      setIsLiked(true);
    } catch (error) {
      console.log("error in liking the product", error);
    }
  };
  const toggleUnlikeProduct = async (productId: any) => {
    try {
      await unLikeProduct(productId);
      setIsLiked(false);
    } catch (error) {
      console.log("error in unliking the product", error);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>{error}</div>
        <button
          className={styles.retryButton}
          onClick={() => navigate(`/series/${seriesId}/product/${productId}`)}
        >
          Try Again
        </button>
      </div>
    );
  }
  if (!product || !series) {
    navigate("/gallery");
    return null;
  }

  const discount =
    product.price > 0
      ? Math.round(((product.price - product.ourPrice) / product.price) * 100)
      : 0;

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate("/gallery")}>Gallery</span>
        <span> / </span>
        <span onClick={() => navigate(`/series/${seriesId}`)}>
          {series.title}
        </span>
        <span> / </span>
        <span>{product.name}</span>
      </div>

      <div className={styles.productHero}>
        <div className={styles.imageGallery}>
          <div className={styles.mainImageContainer} onWheel={handleWheelZoom}>
            {product.picture.length > 0 && (
              <>
                <img
                  src={product.picture[selectedImageIndex].path}
                  alt={`${product.name}`}
                  className={styles.mainProductImage}
                  style={{
                    transform: `scale(${zoomLevel})`,
                    cursor: "zoom-in",
                    transformOrigin: "center center",
                  }}
                  onClick={() => openLightbox(selectedImageIndex)}
                />

                <div className={styles.zoomControls}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoomLevel((prev) => Math.min(prev + 0.1, 3));
                    }}
                    disabled={zoomLevel >= 3}
                  >
                    <FontAwesomeIcon icon={faSearchPlus} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoomLevel((prev) => Math.max(prev - 0.1, 1));
                    }}
                    disabled={zoomLevel <= 1}
                  >
                    <FontAwesomeIcon icon={faSearchMinus} />
                  </button>
                </div>
              </>
            )}

            {discount > 0 && (
              <div className={styles.discountBadge}>{discount}% OFF</div>
            )}
          </div>

          {product.picture.length > 1 && (
            <div className={styles.thumbnailGallery}>
              {product.picture.map((image: any, index: number) => (
                <div
                  key={index}
                  className={`${styles.thumbnailContainer} ${
                    index === selectedImageIndex ? styles.activeThumbnail : ""
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                >
                  <img
                    src={image.path}
                    alt={`Thumbnail ${index + 1}`}
                    className={styles.thumbnail}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.productInfo}>
          <div className={styles.productHeader}>
            {/* <h1 className={styles.productTitle}>{product.name}</h1> */}
            <div className={styles.titleLikeContainer}>
              <h1 className={styles.productTitle}>{product.name}</h1>
              <button
                className={styles.likeButton}
                onClick={async () =>
                  !user
                    ? setShowLoginModal(true)
                    : isLiked
                    ? toggleUnlikeProduct(product?._id)
                    : toggleLikeProduct(product?._id)
                }
                aria-label={
                  isLiked ? "Unlike this product" : "Like this product"
                }
                title={isLiked ? "Remove from Wishlist" : "Add to Wishlist"}
              >
                <FontAwesomeIcon
                  icon={isLiked ? faSolidHeart : faRegularHeart}
                  className={`${styles.heartIcon} ${
                    isLiked ? styles.liked : ""
                  }`}
                />
              </button>
            </div>
            <div className={styles.seriesInfo}>
              <span className={styles.seriesName}>{series.title}</span>
              <span className={styles.productId}>{product.productId}</span>
            </div>

            <div className={styles.pricing}>
              {discount > 0 ? (
                <>
                  <span className={styles.originalPrice}>
                    ₹{product.price.toLocaleString()}
                  </span>
                  <span className={styles.discountedPrice}>
                    ₹{product.ourPrice.toLocaleString()}
                  </span>
                </>
              ) : (
                <span className={styles.regularPrice}>
                  ₹{product.ourPrice.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.detailsGrid}>
              {product.color && (
                <div className={styles.detailItem}>
                  <div>
                    <h3>Color</h3>
                    <p>{product.color}</p>
                  </div>
                </div>
              )}

              {product.attributes &&
                Object.entries(product.attributes).map(([key, value]) => {
                  if (Array.isArray(value)) {
                    return (
                      <div className={styles.detailItem} key={key}>
                        <div>
                          <h3>
                            {key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                          </h3>
                          <ul className={styles.detailList}>
                            {(value as any[]).map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className={styles.detailItem} key={key}>
                      <div>
                        <h3>
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                        </h3>
                        <p>{value?.toString()}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className={styles.descriptionContainer}>
            {renderDescription()}

            {(isDescriptionOverflowing || showFullDescription) && (
              <button
                className={styles.toggleDescription}
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? "Show Less" : "Show More"}
                <FontAwesomeIcon
                  icon={showFullDescription ? faChevronUp : faChevronDown}
                  className={styles.toggleIcon}
                />
              </button>
            )}
          </div>

          <button
            className={styles.enquireButton}
            onClick={() => {
              user === null
                ? setShowLoginModal(true)
                : setShowEnquireModal(true);
            }}
          >
            Enquire About This Product
            <span className={styles.buttonArrow}>→</span>
          </button>
        </div>
      </div>

      {showLightbox && (
        <div className={styles.lightbox}>
          <button className={styles.closeLightbox} onClick={closeLightbox}>
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className={styles.lightboxContent}>
            <button
              className={styles.lightboxNavButton}
              onClick={() => navigateLightbox("prev")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <img
              src={product.picture[lightboxIndex].path}
              alt={`${product.name}`}
              className={styles.lightboxImage}
            />

            <button
              className={styles.lightboxNavButton}
              onClick={() => navigateLightbox("next")}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <div className={styles.lightboxThumbnails}>
            {product.picture.map((image: any, index: number) => (
              <img
                key={index}
                src={image.path}
                alt={`Thumbnail ${index + 1}`}
                className={`${styles.lightboxThumbnail} ${
                  index === lightboxIndex ? styles.activeLightboxThumbnail : ""
                }`}
                onClick={() => setLightboxIndex(index)}
              />
            ))}
          </div>
        </div>
      )}

      {relatedProducts.length > 0 && (
        <div className={styles.relatedProducts}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>More from {series.title}</h2>
            <div className={styles.carouselControls}>
              <button
                className={`${styles.controlButton} ${
                  currentSlide === 0 ? styles.disabled : ""
                }`}
                onClick={() => scrollCarousel("left")}
                disabled={currentSlide === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className={`${styles.controlButton} ${
                  currentSlide >= relatedProducts.length - 3
                    ? styles.disabled
                    : ""
                }`}
                onClick={() => scrollCarousel("right")}
                disabled={currentSlide >= relatedProducts.length - 3}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          <div
            className={styles.carouselContainer}
            ref={carouselRef}
            onMouseEnter={() => setIsCarouselHovered(true)}
            onMouseLeave={() => setIsCarouselHovered(false)}
          >
            <div className={styles.carouselTrack}>
              {relatedProducts.map((product: any) => {
                return (
                  <div
                    key={product._id}
                    className={styles.relatedCard}
                    onClick={() =>
                      navigate(`/series/${seriesId}/product/${product._id}`)
                    }
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <div className={styles.cardImageContainer}>
                      {product.picture?.length > 0 && (
                        <div className={styles.productImageCarousel}>
                          {product.picture.map((image: any, index: number) => (
                            <img
                              key={index}
                              src={image.path}
                              alt={product.name}
                              className={`${styles.cardImage} ${
                                index === currentImageIndex ? styles.active : ""
                              }`}
                            />
                          ))}
                          {product.picture.length > 1 && (
                            <div className={styles.carouselIndicators}>
                              {product.picture.map((_: any, index: number) => (
                                <button
                                  key={index}
                                  className={`${styles.indicator} ${
                                    index === currentImageIndex
                                      ? styles.active
                                      : ""
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentImageIndex(index);
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={styles.cardContent}>
                      <h3>{product.productId}</h3>
                      <p className={styles.cardTitle}>{product.name}</p>
                      <div className={styles.viewButton}>View Details</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showEnquireModal && (
        <EnquireModal
          show={showEnquireModal}
          onHide={() => setShowEnquireModal(false)}
          product={product}
          series={series}
        />
      )}
      {showLoginModal && (
        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
          <ModalBody style={{ padding: "0px", margin: "0px" }}>
            <LoginModal isStandalone={true} />
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default ProductDetailPage;
